import { DesbloqueoService } from '../services/DesbloqueoService';
import React, { useState, useEffect } from 'react';
import { json, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Cookies from 'universal-cookie'
import Swal from 'sweetalert2';
import {
    Box, FormControl, InputLabel, TextField, Button, IconButton, Typography, Paper, MenuItem, Select,
    CircularProgress, Container, Modal, Grid, RadioGroup, FormControlLabel, Radio ,
} from '@mui/material';
import DataTable from 'react-data-table-component';
import { customStyles } from '../Utilities/DataTableStyle';
import '../styles/estilos-tablas.css';
import { name } from 'dayjs/locale/es';
import SearchIcon from '@mui/icons-material/Search';

const PanelDesbloqueo = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const navigate = useNavigate();
    const cookies = new Cookies();
    const usuarioAprobador = cookies.get('userName') || 'Usuario Desconocido';

    const fetchSolicitudes = async () => {
        setLoading(true);
        try {
            const data = await DesbloqueoService.getUnlockRequests();
            setSolicitudes(Array.isArray(data) ? data : []); 
            setError(null); // Limpia cualquier error previo si la llamada fue exitosa
        } catch (err) {
            console.error('Error al cargar solicitudes:', err);
            setSolicitudes([]); // Establece solicitudes a un array vacío
            setError('No se pudieron cargar las solicitudes de desbloqueo.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSolicitudes();
    }, []);

    const handleAprobar = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas aprobar la solicitud ${id}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, aprobar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const fechaAprobacion = new Date().toISOString(); 
                await DesbloqueoService.updateUnlockRequest(id, 'aprobado', fechaAprobacion, usuarioAprobador);
                Swal.fire('¡Aprobada!', 'La solicitud ha sido aprobada.', 'success');
                await fetchSolicitudes();
            } catch (error) {
                Swal.fire('Error', 'No se pudo aprobar la solicitud.', 'error');
            }
        }
    };

    const handleRechazar = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas rechazar la solicitud ${id}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const fechaAprobacion = new Date().toISOString(); 
                await DesbloqueoService.updateUnlockRequest(id, 'rechazado', fechaAprobacion, usuarioAprobador);
                Swal.fire('¡Rechazada!', 'La solicitud ha sido rechazada.', 'success');
                await fetchSolicitudes();
            } catch (error) {
                Swal.fire('Error', 'No se pudo rechazar la solicitud.', 'error');
            }
        }
    };

    const columns = [
        { name: 'RUT', selector: row => row.rut, sortable: true },
        { 
            name: 'Fecha Solicitud', 
            selector: row => row.fecha_solicitud, 
            cell: row => {
                const fecha = new Date(row.fechaSolicitud);
                return isNaN(fecha.getTime()) ? 'Fecha inválida' : fecha.toLocaleString();
            },
            sortable: true 
        },
        { 
            name: 'Usuario Solicitante', 
            selector: row => row.usuarioSolicitante, 
            sortable: true,
            width: '200px'
        },
        { 
            name: 'Estado', 
            selector: row => row.accion, 
            sortable: true 
        },
        {
            name: 'Fecha Aprobación',
            selector: row => row.fechaApruebo,
            cell: row => row.fechaApruebo ? new Date(row.fechaApruebo).toLocaleString() : '-',
            sortable: true
        },
        {
            name: 'Usuario Aprobación',
            selector: row => row.usuarioAprueba,
            cell: row => row.usuarioAprueba || '-',
            sortable: true
        },
        {
            name: 'Acciones',
            cell: row => (
                <Box>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleAprobar(row.id)}
                        sx={{ mr: 1, variant: 'outlined' }}
                        disabled={row.accion !== 'Pendiente'}
                    >
                        Aprobar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRechazar(row.id)}
                        sx={{ mr: 1, variant: 'outlined' }}
                        disabled={row.accion !== 'Pendiente'}
                    >
                        Rechazar
                    </Button>
                </Box>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '200px',
        },
    ];

    const customStyles = {
        table: {
        style: {
            minWidth: '100%',
        },
        },
        headRow: {
        style: {
            backgroundColor: '#F2F2F7',
            borderBottom: 'none',
        },
        },
        headCells: {
        style: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#86868B',
            textTransform: 'uppercase',
        },
        },
        cells: {
        style: {
            fontSize: '14px',
            color: '#1D1D1F',
        },
        },
    };

    const subHeaderComponentMemo = React.useMemo(() => {
        return (
          <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <div className="apple-search-container">
              <SearchIcon className="apple-search-icon" />
              <input
                type="text"
                placeholder="Buscar por Nombre o Rut"
                onChange={e => setFilterText(e.target.value)}
                className="apple-search-input"
              />
            </div>
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
              <FormControl variant="outlined" sx={{ ml: 2, width: '180px' }}>
              <InputLabel shrink htmlFor="start-date" sx={{ backgroundColor: 'white' }}>
                Fecha Solicitud Inicio
              </InputLabel>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  padding: '16.5px 14px',
                  borderRadius: 4,
                  border: '1px solid #c4c4c4',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  background: 'white'
                }}
              />
            </FormControl>
            <FormControl variant="outlined" sx={{ ml: 2, mr:2, width: '180px' }}>
              <InputLabel shrink htmlFor="end-date" sx={{ backgroundColor: 'white' }}>
                Fecha Solicitud Término
              </InputLabel>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  padding: '16.5px 14px',
                  borderRadius: 4,
                  border: '1px solid #c4c4c4',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  background: 'white'
                }}
              />
            </FormControl>
            </Box>
          </Box>
        );
      }, [filterText,startDate, endDate]);

    const filteredItems = solicitudes.filter(item => {
        const matchesText =
            item.usuarioSolicitante.toLowerCase().includes(filterText.toLowerCase()) ||
            item.rut.toLowerCase().includes(filterText.toLowerCase());

        // Filtrado por rango de fecha
        let matchesDate = true;
        if (startDate) {
            const fechaSolicitud = new Date(item.fechaSolicitud);
            const fechaInicio = new Date(startDate);
            fechaInicio.setDate(fechaInicio.getDate() + 1);
            fechaInicio.setHours(0, 0, 0, 0); 
            matchesDate = matchesDate && fechaSolicitud >= fechaInicio;
        }
        if (endDate) {
            const fechaSolicitud = new Date(item.fechaSolicitud);
            // Sumar 1 día para incluir la fecha de término completa
            const fechaFin = new Date(endDate);
            fechaFin.setDate(fechaFin.getDate() + 1); // Sumar 1 día
            fechaFin.setHours(23, 59, 59, 999); // Opcional: inicio del día siguiente
            matchesDate = matchesDate && fechaSolicitud <= fechaFin;
        }

        return matchesText && matchesDate;
    });
    
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Container maxWidth="xl">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h5" sx={{ mb: 4 }}>Panel de Solicitudes de Desbloqueo</Typography>
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    noDataComponent="No hay solicitudes pendientes."  
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30]}    
                    subHeader
                    subHeaderComponent={subHeaderComponentMemo}
                    progressPending={loading}
                    customStyles={customStyles}
                    className='mi-tabla'
                />
            </Paper>
        </Container>
    );
};

export default PanelDesbloqueo;
